import { useEffect, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { ArrowLeft, GitBranch, Layers, FileCode2, AlertCircle, CheckCircle2, Sparkles, RefreshCw, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Format an integer for display ("12345" → "12,345"). Used by every panel.
const fmt = (n) => (typeof n === "number" ? n.toLocaleString() : n);

// Self-serve Discord bot status widget. Polls /api/discord/diag/bot-status
// every 30s. Surfaces all 6 possible health verdicts (kill_switch_off,
// missing_token, missing_guild_id, bot_failed_to_start, guild_mismatch, ok)
// so officers can verify the bot without curl or env access.
function DiscordBotStatusWidget() {
  const [snap, setSnap] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchStatus = async (silent = false) => {
    if (!silent) setRefreshing(true);
    try {
      const res = await fetch(`${API}/discord/diag/bot-status`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || `HTTP ${res.status}`);
      }
      setSnap(await res.json());
      setErr(null);
    } catch (e) {
      setErr(String(e.message || e));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchStatus();
    const t = setInterval(() => fetchStatus(true), 30000);
    return () => clearInterval(t);
  }, []);

  // Map verdict → (color, headline, fix-hint)
  const verdictMeta = (v) => {
    if (!v) return { color: "slate", title: "Unknown", hint: "Loading…" };
    if (v === "ok")
      return { color: "emerald", title: "Bot is online", hint: "All systems normal." };
    if (v.startsWith("kill_switch_off"))
      return {
        color: "amber",
        title: "Kill-switch off",
        hint: "Set DISCORD_BOT_ENABLED=true in production's backend/.env, then restart the backend.",
      };
    if (v === "missing_token")
      return { color: "rose", title: "Bot token missing", hint: "DISCORD_BOT_TOKEN is not set in backend/.env." };
    if (v === "missing_guild_id")
      return { color: "rose", title: "Guild ID missing", hint: "DISCORD_GUILD_ID is not set in backend/.env." };
    if (v === "bot_failed_to_start")
      return { color: "rose", title: "Bot failed to start", hint: "Check backend logs for the actual error." };
    if (v.startsWith("guild_mismatch"))
      return {
        color: "rose",
        title: "Guild mismatch",
        hint: "DISCORD_GUILD_ID doesn't match any guild this bot is in. Verify the ID in the bot's developer portal.",
      };
    return { color: "slate", title: v, hint: "" };
  };

  const meta = verdictMeta(snap?.verdict);
  const accentMap = {
    emerald: "border-emerald-500/40 bg-emerald-900/20",
    amber: "border-amber-500/40 bg-amber-900/20",
    rose: "border-rose-500/40 bg-rose-900/20",
    slate: "border-slate-500/40 bg-slate-900/20",
  };
  const textMap = {
    emerald: "text-emerald-300",
    amber: "text-amber-300",
    rose: "text-rose-300",
    slate: "text-slate-300",
  };

  return (
    <div
      className={`rounded-xl border p-5 mb-6 shadow-xl ${accentMap[meta.color]}`}
      data-testid="discord-bot-status-widget"
    >
      <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
        <div className="flex items-start gap-3">
          <div
            className={`mt-1 w-3 h-3 rounded-full ${
              meta.color === "emerald"
                ? "bg-emerald-400 animate-pulse"
                : meta.color === "amber"
                ? "bg-amber-400"
                : meta.color === "rose"
                ? "bg-rose-400"
                : "bg-slate-400"
            }`}
            data-testid="bot-status-pulse"
          />
          <div>
            <h3 className={`font-semibold ${textMap[meta.color]}`} data-testid="bot-status-title">
              Discord Bot · {meta.title}
            </h3>
            <p className="text-xs text-slate-400 mt-0.5" data-testid="bot-status-hint">
              {meta.hint}
            </p>
            {snap?.checked_at && (
              <p className="text-[10px] text-slate-500 mt-0.5 font-mono">
                Last checked {new Date(snap.checked_at).toLocaleTimeString()}
                {refreshing ? " · refreshing…" : ""}
              </p>
            )}
          </div>
        </div>
        <button
          onClick={() => fetchStatus()}
          disabled={refreshing}
          className="text-xs px-3 py-1 rounded border border-slate-700 hover:bg-slate-800 disabled:opacity-50 text-slate-200"
          data-testid="bot-status-refresh-btn"
        >
          {refreshing ? "Refreshing…" : "Refresh now"}
        </button>
      </div>

      {loading && !snap && (
        <p className="text-xs text-slate-400">Loading bot status…</p>
      )}

      {err && (
        <p className="text-xs text-rose-300 font-mono" data-testid="bot-status-error">
          {err}
        </p>
      )}

      {snap && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px] mt-2">
          <div className="bg-slate-900/40 rounded px-2 py-1.5 border border-slate-700/40">
            <p className="text-slate-400">DISCORD_BOT_ENABLED</p>
            <p
              className={`font-mono font-medium ${
                snap.bot_enabled_env ? "text-emerald-300" : "text-amber-300"
              }`}
              data-testid="bot-flag-status"
            >
              {snap.bot_enabled_env_raw}
            </p>
          </div>
          <div className="bg-slate-900/40 rounded px-2 py-1.5 border border-slate-700/40">
            <p className="text-slate-400">Bot process</p>
            <p
              className={`font-mono font-medium ${
                snap.bot_running ? "text-emerald-300" : "text-rose-300"
              }`}
              data-testid="bot-process-status"
            >
              {snap.bot_running ? snap.bot_user || "running" : "not running"}
            </p>
          </div>
          <div className="bg-slate-900/40 rounded px-2 py-1.5 border border-slate-700/40">
            <p className="text-slate-400">Guild</p>
            <p
              className={`font-mono font-medium truncate ${
                snap.guild_found ? "text-emerald-300" : "text-slate-400"
              }`}
              data-testid="bot-guild-status"
              title={snap.guild_name || "—"}
            >
              {snap.guild_name || "—"}
              {snap.member_count ? ` (${fmt(snap.member_count)})` : ""}
            </p>
          </div>
          <div className="bg-slate-900/40 rounded px-2 py-1.5 border border-slate-700/40">
            <p className="text-slate-400">Open voice sessions</p>
            <p className="font-mono font-medium text-slate-200" data-testid="bot-open-sessions">
              {fmt(snap.open_voice_sessions_count)}
              {snap.voice_channels_count
                ? ` · ${snap.voice_channels_count} voice ch.`
                : ""}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, sublabel, accent = "cyan", testid }) {
  const accentBorder = {
    cyan: "border-cyan-500/40",
    amber: "border-amber-500/40",
    emerald: "border-emerald-500/40",
    rose: "border-rose-500/40",
  }[accent];
  const accentText = {
    cyan: "text-cyan-300",
    amber: "text-amber-300",
    emerald: "text-emerald-300",
    rose: "text-rose-300",
  }[accent];
  return (
    <div
      data-testid={testid}
      className={`bg-slate-800/80 rounded-xl border ${accentBorder} px-5 py-4 shadow-lg`}
    >
      <p className="text-xs uppercase tracking-wider text-slate-400">{label}</p>
      <p className={`mt-1 text-3xl font-bold ${accentText}`}>{value}</p>
      {sublabel && <p className="mt-1 text-xs text-slate-400">{sublabel}</p>}
    </div>
  );
}

function ProgressBar({ pct }) {
  return (
    <div className="w-full bg-slate-700/50 rounded-full h-3 overflow-hidden">
      <div
        className="h-full bg-gradient-to-r from-cyan-500 via-cyan-400 to-emerald-400 transition-all duration-700"
        style={{ width: `${Math.min(100, pct)}%` }}
        data-testid="refactor-progress-fill"
      />
    </div>
  );
}

const METHOD_COLORS = {
  GET: "bg-emerald-900/40 text-emerald-300 border-emerald-700/60",
  POST: "bg-cyan-900/40 text-cyan-300 border-cyan-700/60",
  PUT: "bg-amber-900/40 text-amber-300 border-amber-700/60",
  DELETE: "bg-rose-900/40 text-rose-300 border-rose-700/60",
  PATCH: "bg-violet-900/40 text-violet-300 border-violet-700/60",
};

// Parse the `extracted_at` tag (e.g. "2026-02 (Pass 5b-iii)" or
// "Pre-refactor") into a sortable numeric key.
// Pre-refactor → 0, Pass 1 → 100, Pass 5a → 500, Pass 5b → 501,
// Pass 5b-ii → 502, Pass 5b-iii → 503, Pass 6 → 600, Pass 11 → 1100.
function passSortKey(label) {
  if (!label) return 9999;
  const s = String(label).toLowerCase();
  if (s.includes("pre-refactor")) return 0;
  const m = s.match(/pass\s+(\d+)([a-z])?(?:-(i+|iv|v))?/);
  if (!m) return 9998;
  const major = parseInt(m[1], 10) * 100;
  const minor = m[2] ? m[2].charCodeAt(0) - 96 : 0; // a=1, b=2…
  const romanMap = { i: 1, ii: 2, iii: 3, iv: 4, v: 5 };
  const sub = m[3] ? (romanMap[m[3]] || 0) : 0;
  return major + minor * 10 + sub;
}

// Build the trend chart: cumulative extracted-module lines after each pass.
// Honest caveat: module line counts include their own boilerplate (imports,
// init_module, dep wiring) so the cumulative number runs ~10% ahead of the
// pure-delete delta. The shape of the curve is still the right story.
function RefactorTrendChart({ modules, baselineLines, currentServerLines }) {
  if (!modules || modules.length === 0) return null;

  // Group modules by pass label (a single "pass" may include >1 module, e.g. Pass 7 = prospects + hangarounds)
  const byPass = new Map();
  modules.forEach((m) => {
    const key = m.extracted_at || "Unknown";
    if (!byPass.has(key)) byPass.set(key, { label: key, modules: [], lines: 0 });
    const bucket = byPass.get(key);
    bucket.modules.push(m.name);
    bucket.lines += m.lines || 0;
  });

  // Sort passes chronologically
  const passes = Array.from(byPass.values()).sort(
    (a, b) => passSortKey(a.label) - passSortKey(b.label)
  );

  // Compute cumulative extracted + projected server.py shrink per pass.
  // The "implied server.py" line is baseline minus cumulative — useful as
  // a target curve even though the actual current value is reported live.
  let cumExtracted = 0;
  const points = passes.map((p) => {
    cumExtracted += p.lines;
    return {
      label: p.label.replace(/^\d{4}-\d{2}\s*/, "").replace(/[()]/g, "").trim(),
      modules: p.modules,
      passLines: p.lines,
      cumulative: cumExtracted,
      impliedServer: Math.max(0, baselineLines - cumExtracted),
    };
  });

  // SVG layout
  const W = 760;
  const H = 220;
  const PAD = { top: 20, right: 28, bottom: 36, left: 56 };
  const innerW = W - PAD.left - PAD.right;
  const innerH = H - PAD.top - PAD.bottom;
  const maxY = baselineLines;
  const minY = Math.min(...points.map((p) => p.impliedServer), currentServerLines || maxY);
  const yRange = Math.max(1, maxY - 0); // anchor floor at 0 for clearer story
  const xStep = points.length > 1 ? innerW / (points.length - 1) : innerW;

  const xOf = (i) => PAD.left + i * xStep;
  const yOf = (v) => PAD.top + innerH - ((v - 0) / yRange) * innerH;

  // Baseline horizontal line position
  const yBaseline = yOf(baselineLines);
  const yCurrent = yOf(currentServerLines);

  // Line path
  const linePath = points
    .map((p, i) => `${i === 0 ? "M" : "L"} ${xOf(i).toFixed(1)} ${yOf(p.impliedServer).toFixed(1)}`)
    .join(" ");
  const areaPath =
    `M ${xOf(0).toFixed(1)} ${yOf(baselineLines).toFixed(1)} ` +
    points.map((p, i) => `L ${xOf(i).toFixed(1)} ${yOf(p.impliedServer).toFixed(1)}`).join(" ") +
    ` L ${xOf(points.length - 1).toFixed(1)} ${yOf(0).toFixed(1)} L ${xOf(0).toFixed(1)} ${yOf(0).toFixed(1)} Z`;

  // Y-axis ticks (5 evenly spaced)
  const yTicks = [];
  const tickCount = 5;
  for (let i = 0; i <= tickCount; i++) {
    const v = Math.round((maxY / tickCount) * i);
    yTicks.push({ v, y: yOf(v) });
  }

  return (
    <div
      className="bg-slate-800/80 rounded-xl border border-slate-700 p-5 mb-6 shadow-xl"
      data-testid="refactor-trend-chart"
    >
      <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
        <div>
          <h3 className="text-slate-100 font-semibold flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-cyan-400" />
            server.py shrink over time
          </h3>
          <p className="text-xs text-slate-400 mt-0.5">
            Implied <span className="font-mono">server.py</span> size = baseline (
            {baselineLines.toLocaleString()}) − cumulative extracted lines, per refactor pass.
          </p>
        </div>
        <div className="flex items-center gap-4 text-xs flex-wrap">
          <div className="flex items-center gap-1.5">
            <span className="w-3 h-0.5 bg-cyan-400 inline-block" />
            <span className="text-slate-300">Implied server.py</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-3 h-0.5 border-t border-dashed border-emerald-400 inline-block" />
            <span className="text-slate-300">Today ({currentServerLines.toLocaleString()})</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-3 h-0.5 border-t border-dashed border-amber-400 inline-block" />
            <span className="text-slate-300">Baseline</span>
          </div>
        </div>
      </div>

      <div className="overflow-x-auto -mx-1">
        <svg
          viewBox={`0 0 ${W} ${H}`}
          className="w-full h-auto"
          preserveAspectRatio="xMidYMid meet"
          data-testid="refactor-trend-svg"
        >
          <defs>
            <linearGradient id="trendArea" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#22d3ee" stopOpacity="0.35" />
              <stop offset="100%" stopColor="#22d3ee" stopOpacity="0.02" />
            </linearGradient>
          </defs>

          {/* Y-axis ticks + gridlines */}
          {yTicks.map((t, i) => (
            <g key={`ytick-${i}`}>
              <line
                x1={PAD.left}
                x2={W - PAD.right}
                y1={t.y}
                y2={t.y}
                stroke="#334155"
                strokeWidth="1"
                strokeDasharray="2 4"
              />
              <text
                x={PAD.left - 8}
                y={t.y + 4}
                fontSize="10"
                fill="#94a3b8"
                textAnchor="end"
                fontFamily="ui-monospace, monospace"
              >
                {t.v.toLocaleString()}
              </text>
            </g>
          ))}

          {/* Baseline reference (amber dashed) */}
          <line
            x1={PAD.left}
            x2={W - PAD.right}
            y1={yBaseline}
            y2={yBaseline}
            stroke="#f59e0b"
            strokeWidth="1.2"
            strokeDasharray="6 4"
          />
          {/* Current actual reference (emerald dashed) */}
          <line
            x1={PAD.left}
            x2={W - PAD.right}
            y1={yCurrent}
            y2={yCurrent}
            stroke="#34d399"
            strokeWidth="1.2"
            strokeDasharray="6 4"
          />

          {/* Area under curve */}
          <path d={areaPath} fill="url(#trendArea)" />
          {/* Line */}
          <path d={linePath} fill="none" stroke="#22d3ee" strokeWidth="2.2" />

          {/* Points + x-axis labels */}
          {points.map((p, i) => {
            const cx = xOf(i);
            const cy = yOf(p.impliedServer);
            return (
              <g key={`pt-${i}`} data-testid={`trend-point-${i}`}>
                <circle cx={cx} cy={cy} r="4" fill="#0f172a" stroke="#22d3ee" strokeWidth="2" />
                <title>
                  {p.label} — {p.impliedServer.toLocaleString()} lines (cumulative extracted{" "}
                  {p.cumulative.toLocaleString()})
                </title>
                <text
                  x={cx}
                  y={H - PAD.bottom + 14}
                  fontSize="9.5"
                  fill="#cbd5e1"
                  textAnchor="middle"
                  fontFamily="ui-sans-serif, system-ui"
                >
                  {p.label.replace(/^Pass\s*/i, "P")}
                </text>
              </g>
            );
          })}
        </svg>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3 text-[11px]">
        <div className="bg-slate-900/40 rounded px-2 py-1.5 border border-slate-700/40">
          <p className="text-slate-400">First pass</p>
          <p className="text-slate-200 font-medium">{points[0]?.label || "—"}</p>
        </div>
        <div className="bg-slate-900/40 rounded px-2 py-1.5 border border-slate-700/40">
          <p className="text-slate-400">Latest pass</p>
          <p className="text-slate-200 font-medium">{points[points.length - 1]?.label || "—"}</p>
        </div>
        <div className="bg-slate-900/40 rounded px-2 py-1.5 border border-slate-700/40">
          <p className="text-slate-400">Total passes</p>
          <p className="text-slate-200 font-medium">{points.length}</p>
        </div>
        <div className="bg-slate-900/40 rounded px-2 py-1.5 border border-slate-700/40">
          <p className="text-slate-400">Avg / pass</p>
          <p className="text-slate-200 font-medium">
            {points.length > 0
              ? Math.round(points[points.length - 1].cumulative / points.length).toLocaleString()
              : "—"}{" "}
            lines
          </p>
        </div>
      </div>
    </div>
  );
}


// Module leaderboard — pairs with the trend chart above to make the
// "where did the 10k lines go" story visible at a glance.
// Top section: per-pass lines-extracted sparkline (velocity).
// Bottom section: top-N modules sorted by (endpoint_count desc, lines desc)
// with horizontal bars. The two together turn the dashboard into a one-glance
// "what's been done" view that supplements the existing detailed cards list.
function ModuleLeaderboard({ modules }) {
  if (!modules || modules.length === 0) return null;

  // ── Velocity sparkline data: lines extracted per pass ─────────────
  // Group modules by extracted_at label (matches the trend chart's grouping)
  const byPass = new Map();
  modules.forEach((m) => {
    const key = m.extracted_at || "Unknown";
    if (!byPass.has(key)) byPass.set(key, { label: key, lines: 0, modules: 0 });
    const b = byPass.get(key);
    b.lines += m.lines || 0;
    b.modules += 1;
  });
  const passes = Array.from(byPass.values()).sort(
    (a, b) => passSortKey(a.label) - passSortKey(b.label),
  );
  // Skip "Pre-refactor" buckets for velocity — they represent baseline modules
  // (e.g. /events, /downloads) that pre-dated the refactor and would distort the
  // "how fast are we extracting" question.
  const velocityPasses = passes.filter(
    (p) => !p.label.toLowerCase().includes("pre-refactor"),
  );

  const maxV = Math.max(1, ...velocityPasses.map((p) => p.lines));
  const totalExtractedSinceStart = velocityPasses.reduce((s, p) => s + p.lines, 0);
  const avgPerPass = velocityPasses.length
    ? Math.round(totalExtractedSinceStart / velocityPasses.length)
    : 0;
  const biggestPass = velocityPasses.reduce(
    (max, p) => (p.lines > (max?.lines || 0) ? p : max),
    null,
  );

  // ── Leaderboard data: top 8 modules by endpoint count then by lines ───
  const leaderboard = [...modules]
    .sort((a, b) => {
      if (b.endpoint_count !== a.endpoint_count) return b.endpoint_count - a.endpoint_count;
      return (b.lines || 0) - (a.lines || 0);
    })
    .slice(0, 8);
  const maxBarValue = Math.max(...leaderboard.map((m) => m.lines || 0), 1);

  // Color band for the bar based on rank (top 3 stand out).
  const barFill = (idx) => {
    if (idx === 0) return "from-emerald-500/70 to-emerald-400/40";
    if (idx === 1) return "from-cyan-500/70 to-cyan-400/40";
    if (idx === 2) return "from-violet-500/70 to-violet-400/40";
    return "from-slate-500/60 to-slate-400/30";
  };

  return (
    <div
      className="bg-slate-800/80 rounded-xl border border-slate-700 p-5 mb-6 shadow-xl"
      data-testid="refactor-module-leaderboard"
    >
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div className="flex items-start gap-3">
          <Layers className="w-5 h-5 text-cyan-400 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="text-slate-100 font-semibold">Where did the lines go?</h3>
            <p className="text-xs text-slate-400 mt-0.5">
              Per-pass velocity + top extracted modules. Pair this with the trend
              chart above for the full "extraction story".
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap text-[11px]">
          <span className="px-2 py-1 rounded-full bg-slate-900/40 text-slate-300 border border-slate-700/60">
            {velocityPasses.length} passes
          </span>
          <span className="px-2 py-1 rounded-full bg-cyan-900/30 text-cyan-300 border border-cyan-700/60">
            avg {fmt(avgPerPass)} ln/pass
          </span>
          {biggestPass && (
            <span
              className="px-2 py-1 rounded-full bg-emerald-900/30 text-emerald-300 border border-emerald-700/60"
              title={`Biggest pass: ${biggestPass.label} — ${fmt(biggestPass.lines)} lines`}
            >
              biggest:&nbsp;
              {biggestPass.label.replace(/^Pass\s*/i, "P").replace(/2026-\d{2}\s*/, "").replace(/[()]/g, "")}
              &nbsp;({fmt(biggestPass.lines)})
            </span>
          )}
        </div>
      </div>

      {/* ── Velocity sparkline (bar chart) ─────────────────────────────── */}
      <div className="mb-5" data-testid="leaderboard-velocity">
        <p className="text-[11px] uppercase tracking-wider text-slate-400 mb-2">
          Lines extracted per pass
        </p>
        <div className="flex items-end gap-1 h-24">
          {velocityPasses.map((p, i) => {
            const hPct = Math.max(4, Math.round((p.lines / maxV) * 100));
            const compact = p.label
              .replace(/^Pass\s*/i, "P")
              .replace(/2026-\d{2}\s*/, "")
              .replace(/[()]/g, "")
              .trim();
            return (
              <div
                key={`vel-${i}`}
                className="flex-1 flex flex-col items-center justify-end min-w-0"
                title={`${p.label}: ${fmt(p.lines)} lines, ${p.modules} module(s)`}
                data-testid={`leaderboard-vel-bar-${i}`}
              >
                <span className="text-[9px] text-slate-500 mb-0.5 leading-none">
                  {fmt(p.lines)}
                </span>
                <div
                  className="w-full rounded-t bg-gradient-to-t from-cyan-600/70 to-cyan-400/40 border-t border-cyan-400/50"
                  style={{ height: `${hPct}%` }}
                />
                <span className="text-[9px] text-slate-400 mt-1 truncate w-full text-center leading-none">
                  {compact}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Top-N module leaderboard with horizontal bars ─────────────── */}
      <div data-testid="leaderboard-modules">
        <div className="flex items-center justify-between mb-2">
          <p className="text-[11px] uppercase tracking-wider text-slate-400">
            Top modules — sorted by endpoints, then lines
          </p>
          <span className="text-[10px] text-slate-500">
            showing {leaderboard.length} of {modules.length}
          </span>
        </div>
        <ol className="space-y-1.5">
          {leaderboard.map((m, i) => {
            const pct = Math.round(((m.lines || 0) / maxBarValue) * 100);
            const shortDir = m.dir.replace(/^routes\//, "");
            return (
              <li
                key={m.dir}
                className="relative flex items-center gap-2 group"
                data-testid={`leaderboard-row-${m.dir.replace(/\//g, "-")}`}
              >
                <span className="text-[10px] font-mono text-slate-500 w-5 text-right flex-shrink-0">
                  {i + 1}.
                </span>
                <div className="flex-1 min-w-0 relative h-7 bg-slate-900/40 rounded border border-slate-700/40 overflow-hidden">
                  <div
                    className={`absolute inset-y-0 left-0 bg-gradient-to-r ${barFill(i)} rounded`}
                    style={{ width: `${Math.max(4, pct)}%` }}
                  />
                  <div className="absolute inset-0 flex items-center justify-between px-2 z-10">
                    <span className="text-xs font-mono text-slate-100 truncate flex-1 mr-2">
                      {shortDir}
                    </span>
                    <span className="text-[10px] text-slate-300 font-mono whitespace-nowrap">
                      {m.endpoint_count}&nbsp;ep · {fmt(m.lines)}&nbsp;ln
                    </span>
                  </div>
                </div>
              </li>
            );
          })}
        </ol>
      </div>
    </div>
  );
}

function DataHygienePanel() {
  const [hygiene, setHygiene] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyKey, setBusyKey] = useState(null);
  const [confirmKey, setConfirmKey] = useState(null);
  const [flash, setFlash] = useState(null);
  const [history, setHistory] = useState([]);

  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [scanRes, histRes] = await Promise.all([
        axios.get(`${API}/admin/data-hygiene/scan`, { headers: authHeaders() }),
        axios.get(`${API}/admin/data-hygiene/history?limit=20`, { headers: authHeaders() }).catch(() => ({ data: { history: [] } })),
      ]);
      setHygiene(scanRes.data);
      setHistory(histRes.data?.history || []);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || "Failed to scan");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const runFix = async (key) => {
    setBusyKey(key);
    setFlash(null);
    try {
      const res = await axios.post(`${API}/admin/data-hygiene/fix/${key}`, {}, { headers: authHeaders() });
      const cleaned = res.data?.result?.cleaned ?? 0;
      setFlash({ kind: "ok", text: `Cleaned ${cleaned} record${cleaned === 1 ? "" : "s"} for "${key}".` });
      setConfirmKey(null);
      await load();
    } catch (err) {
      setFlash({ kind: "err", text: err.response?.data?.detail || err.message || "Fix failed" });
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <div className="bg-slate-800/80 rounded-xl border border-slate-700 shadow-lg overflow-hidden mb-6" data-testid="data-hygiene-panel">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-5 py-4 border-b border-slate-700/60">
        <div className="flex items-start gap-3">
          <Sparkles className="w-5 h-5 text-violet-400 mt-0.5 flex-shrink-0" />
          <div>
            <h2 className="font-semibold text-slate-100">Data Hygiene</h2>
            <p className="text-xs text-slate-400 mt-0.5">
              Scans for legacy data quirks and offers one-click cleanups.
              {hygiene && !loading && (
                <> Last scan: <span className="text-slate-300">{new Date(hygiene.scanned_at).toLocaleString()}</span></>
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {hygiene && (
            <span
              className={`text-xs px-2.5 py-1 rounded-full border ${
                hygiene.total === 0
                  ? "bg-emerald-900/40 text-emerald-300 border-emerald-700/60"
                  : "bg-amber-900/40 text-amber-300 border-amber-700/60"
              }`}
              data-testid="data-hygiene-total-badge"
            >
              {hygiene.total === 0 ? "All clean" : `${hygiene.total} issue${hygiene.total === 1 ? "" : "s"}`}
            </span>
          )}
          <Button
            size="sm"
            variant="outline"
            onClick={load}
            disabled={loading}
            className="border-slate-600 text-slate-200 hover:bg-slate-700"
            data-testid="data-hygiene-rescan-btn"
          >
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            <span className="ml-1.5">Re-scan</span>
          </Button>
        </div>
      </div>

      {error && (
        <div className="px-5 py-3 text-sm text-rose-300 bg-rose-900/20 border-b border-rose-800/40" data-testid="data-hygiene-error">
          {error}
        </div>
      )}

      {flash && (
        <div
          className={`px-5 py-2.5 text-sm border-b ${
            flash.kind === "ok"
              ? "text-emerald-200 bg-emerald-900/20 border-emerald-800/40"
              : "text-rose-200 bg-rose-900/20 border-rose-800/40"
          }`}
          data-testid="data-hygiene-flash"
        >
          {flash.text}
        </div>
      )}

      <div className="divide-y divide-slate-700/50">
        {hygiene?.issues?.map((issue) => {
          const isBusy = busyKey === issue.key;
          const isConfirming = confirmKey === issue.key;
          const hasCount = issue.count > 0;
          return (
            <div
              key={issue.key}
              className="px-5 py-4"
              data-testid={`hygiene-issue-${issue.key}`}
            >
              <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-semibold text-slate-100 text-sm">{issue.label}</h3>
                    <span
                      className={`text-[11px] font-bold px-2 py-0.5 rounded-full border ${
                        hasCount
                          ? "bg-amber-900/40 text-amber-300 border-amber-700/60"
                          : "bg-emerald-900/40 text-emerald-300 border-emerald-700/60"
                      }`}
                    >
                      {issue.count}
                    </span>
                  </div>
                  <p className="text-xs text-slate-400 mt-1 font-mono">{issue.key}</p>
                  <p className="text-xs text-slate-300 mt-1">{issue.description}</p>
                  {issue.error && (
                    <p className="text-xs text-rose-300 mt-1">Scan error: {issue.error}</p>
                  )}
                  {hasCount && issue.samples?.length > 0 && (
                    <details className="mt-2">
                      <summary className="text-xs text-slate-400 cursor-pointer hover:text-slate-200 select-none">
                        Show {issue.samples.length} sample{issue.samples.length === 1 ? "" : "s"}
                      </summary>
                      <pre className="mt-2 text-[11px] text-slate-300 bg-slate-900/60 rounded p-2 overflow-x-auto border border-slate-700/50">
{JSON.stringify(issue.samples, null, 2)}
                      </pre>
                    </details>
                  )}
                </div>
                <div className="flex-shrink-0 flex items-center gap-2">
                  {hasCount && !isConfirming && (
                    <Button
                      size="sm"
                      onClick={() => setConfirmKey(issue.key)}
                      disabled={isBusy}
                      className="bg-violet-700 hover:bg-violet-600 text-white border-violet-600"
                      data-testid={`hygiene-fix-btn-${issue.key}`}
                    >
                      Clean up
                    </Button>
                  )}
                  {isConfirming && (
                    <>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setConfirmKey(null)}
                        disabled={isBusy}
                        className="border-slate-600 text-slate-200 hover:bg-slate-700"
                        data-testid={`hygiene-cancel-btn-${issue.key}`}
                      >
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => runFix(issue.key)}
                        disabled={isBusy}
                        className="bg-rose-700 hover:bg-rose-600 text-white border-rose-600"
                        data-testid={`hygiene-confirm-btn-${issue.key}`}
                      >
                        {isBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : null}
                        Confirm
                      </Button>
                    </>
                  )}
                  {!hasCount && (
                    <span className="text-xs text-emerald-300/80">Clean ✓</span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
        {loading && !hygiene && (
          <div className="px-5 py-6 text-sm text-slate-400" data-testid="hygiene-loading">
            Scanning…
          </div>
        )}
      </div>

      {/* Audit trail — last 20 cleanups */}
      {history.length > 0 && (
        <div className="border-t border-slate-700/60 bg-slate-900/40 px-5 py-3" data-testid="hygiene-history">
          <p className="text-[11px] uppercase tracking-wider text-slate-500 mb-2">
            Recent activity (last {history.length})
          </p>
          <ul className="space-y-1 max-h-40 overflow-y-auto pr-1">
            {history.map((h) => (
              <li
                key={h.id}
                className="flex items-center gap-2 text-xs text-slate-300 font-mono"
                data-testid={`hygiene-history-row-${h.id}`}
              >
                <span className="text-slate-500 tabular-nums">
                  {new Date(h.fixed_at).toLocaleString(undefined, {
                    month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
                  })}
                </span>
                <span className="text-slate-200">{h.fixed_by}</span>
                <span className="text-slate-500">→</span>
                <span className="text-violet-300">{h.issue_key}</span>
                <span
                  className={`ml-auto text-[11px] px-1.5 py-0.5 rounded border ${
                    h.cleaned > 0
                      ? "bg-emerald-900/40 text-emerald-300 border-emerald-700/60"
                      : "bg-slate-800/60 text-slate-400 border-slate-700"
                  }`}
                >
                  {h.cleaned} cleaned
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default function RefactorHealth() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(`${API}/admin/refactor-health`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setData(res.data);
      } catch (err) {
        setError(err.response?.data?.detail || err.message || "Failed to load");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <nav className="bg-slate-800 border-b border-slate-700 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2 sm:gap-4">
              <Button
                onClick={() => navigate("/")}
                variant="outline"
                size="sm"
                className="flex items-center gap-1 sm:gap-2 border-slate-600 text-slate-200 hover:bg-slate-700"
                data-testid="refactor-health-back-btn"
              >
                <ArrowLeft className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="hidden sm:inline">Back</span>
              </Button>
              <div className="flex items-center gap-2">
                <GitBranch className="w-5 h-5 sm:w-6 sm:h-6 text-cyan-400" />
                <h1 className="text-lg sm:text-2xl font-bold text-slate-100">
                  Refactor Health
                </h1>
              </div>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8" data-testid="refactor-health-page">
        {loading && (
          <div className="text-slate-300 text-sm" data-testid="refactor-health-loading">
            Loading refactor stats…
          </div>
        )}

        {error && (
          <div
            className="flex items-start gap-3 bg-rose-900/30 border border-rose-700/60 rounded-lg p-4 text-rose-200"
            data-testid="refactor-health-error"
          >
            <AlertCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-semibold">Couldn't load refactor health.</p>
              <p className="text-sm text-rose-300/80">{error}</p>
            </div>
          </div>
        )}

        {data && (
          <>
            {/* Headline progress */}
            <div className="bg-slate-800/80 rounded-xl border border-slate-700 p-6 mb-6 shadow-xl">
              <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-4">
                <div>
                  <p className="text-xs uppercase tracking-wider text-slate-400">
                    server.py shrink progress
                  </p>
                  <p className="text-4xl font-bold text-cyan-300 mt-1" data-testid="refactor-progress-pct">
                    {data.progress_pct}%
                  </p>
                  <p className="text-xs text-slate-400 mt-1">
                    {fmt(data.lines_removed)} of {fmt(data.baseline_lines)} baseline lines extracted
                  </p>
                </div>
                <div className="text-right text-sm text-slate-400">
                  <p>
                    Baseline:{" "}
                    <span className="text-slate-200">{fmt(data.baseline_lines)} lines</span>
                  </p>
                  <p>
                    Today:{" "}
                    <span className="text-slate-200">{fmt(data.server_py_lines)} lines</span>
                  </p>
                </div>
              </div>
              <ProgressBar pct={data.progress_pct} />
            </div>

            {/* Discord Bot Status — self-serve diagnostic widget */}
            <DiscordBotStatusWidget />

            {/* Trend chart — server.py shrink over time per pass */}
            <RefactorTrendChart
              modules={data.modules}
              baselineLines={data.baseline_lines}
              currentServerLines={data.server_py_lines}
            />

            {/* Module leaderboard — velocity sparkline + top modules */}
            <ModuleLeaderboard modules={data.modules} />

            {/* Stats grid */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <StatCard
                label="server.py lines"
                value={fmt(data.server_py_lines)}
                sublabel={`${fmt(data.lines_removed)} removed`}
                accent="amber"
                testid="stat-server-lines"
              />
              <StatCard
                label="server.py endpoints"
                value={fmt(data.server_py_endpoints)}
                sublabel="still inline"
                accent="amber"
                testid="stat-server-endpoints"
              />
              <StatCard
                label="Extracted lines"
                value={fmt(data.extracted_total_lines)}
                sublabel={`${data.modules.length} modules`}
                accent="cyan"
                testid="stat-extracted-lines"
              />
              <StatCard
                label="Extracted endpoints"
                value={fmt(data.extracted_total_endpoints)}
                sublabel="modularised"
                accent="emerald"
                testid="stat-extracted-endpoints"
              />
            </div>

            {/* Data Hygiene panel (national officers only — gated server-side) */}
            <DataHygienePanel />

            {/* Modules */}
            <div className="flex items-center gap-2 mb-3">
              <Layers className="w-5 h-5 text-cyan-400" />
              <h2 className="text-lg font-semibold text-slate-100">Extracted Modules</h2>
            </div>

            <div className="space-y-4" data-testid="refactor-modules-list">
              {data.modules.map((mod) => (
                <div
                  key={mod.dir}
                  className="bg-slate-800/80 rounded-xl border border-slate-700 shadow-lg overflow-hidden"
                  data-testid={`module-card-${mod.dir.replace(/\//g, "-")}`}
                >
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-5 py-4 border-b border-slate-700/60">
                    <div className="flex items-start gap-3">
                      <FileCode2 className="w-5 h-5 text-cyan-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <h3 className="font-semibold text-slate-100">{mod.name}</h3>
                        <p className="text-xs text-slate-400 font-mono mt-0.5">
                          backend/{mod.dir}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2 flex-wrap sm:justify-end">
                      <span className="text-xs px-2.5 py-1 rounded-full bg-slate-700/70 text-slate-300 border border-slate-600">
                        {fmt(mod.lines)} lines
                      </span>
                      <span className="text-xs px-2.5 py-1 rounded-full bg-cyan-900/40 text-cyan-300 border border-cyan-700/60">
                        {mod.endpoint_count} endpoints
                      </span>
                      <span className="text-xs px-2.5 py-1 rounded-full bg-slate-700/40 text-slate-400 border border-slate-700">
                        {mod.extracted_at}
                      </span>
                    </div>
                  </div>

                  <div className="px-5 py-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
                      {mod.endpoints.map((ep, i) => (
                        <div
                          key={`${ep.method}-${ep.path}-${i}`}
                          className="flex items-center gap-2 text-sm"
                        >
                          <span
                            className={`text-[10px] font-bold tracking-wider px-2 py-0.5 rounded border ${
                              METHOD_COLORS[ep.method] || METHOD_COLORS.GET
                            }`}
                          >
                            {ep.method}
                          </span>
                          <span className="font-mono text-slate-300 truncate">
                            {ep.path}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 flex items-start gap-3 bg-slate-800/60 border border-slate-700/60 rounded-lg p-4">
              <CheckCircle2 className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-slate-300">
                <p className="font-semibold text-slate-100 mb-1">How this number moves</p>
                <p>
                  Every refactor pass carves a logical domain (mobile, dues-reminders,
                  polls, events…) out of <span className="font-mono">server.py</span> into
                  its own router under{" "}
                  <span className="font-mono">backend/routes/</span>. The progress bar
                  reflects the cumulative line delta against the{" "}
                  {fmt(data.baseline_lines)}-line baseline captured at the start of the
                  refactor.
                </p>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
